import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from './services/auth.service';
import { Observable } from 'rxjs';
import { map, take, filter, switchMap } from 'rxjs/operators';

/**
 * Guard para redirigir usuarios ya autenticados lejos de las páginas de onboarding.
 *
 * Previene que usuarios logueados accedan a páginas de login, registro, etc.
 * Si el usuario ya está autenticado, preserva el parámetro redirect si existe,
 * o redirige a /home por defecto.
 *
 * Espera a que AuthService termine de inicializar antes de verificar el estado
 * de autenticación para evitar race conditions.
 *
 * @example
 * // En onboarding-routing.module.ts:
 * {
 *   path: 'login',
 *   component: LoginComponent,
 *   canActivate: [LoggedInRedirectGuard]
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class LoggedInRedirectGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Esperar a que AuthService termine de inicializar completamente
    return this.authService.initialized$.pipe(
      filter(initialized => initialized === true),  // Esperar hasta que sea true
      take(1),
      switchMap(() => this.authService.isLoggedIn$),  // Luego verificar si está logueado
      take(1),
      map(isLoggedIn => {
        if (isLoggedIn) {
          // Usuario ya autenticado - verificar si hay redirect param
          const redirectParam = route.queryParams['redirect'];

          if (redirectParam) {
            // Hay redirect, decodificar y validar
            try {
              const decodedUrl = decodeURIComponent(redirectParam);

              // Validar URL (reusar lógica de login.component)
              if (this.isValidRedirectUrl(decodedUrl)) {
                return this.router.createUrlTree([decodedUrl]);
              }
            } catch (error) {
              console.error('LoggedInRedirectGuard: Error al decodificar redirect:', error);
            }
          }

          // Sin redirect válido, ir a home
          return this.router.createUrlTree(['/home']);
        } else {
          // Usuario no autenticado - permitir acceso a la página de onboarding
          return true;
        }
      })
    );
  }

  /**
   * Valida que una URL de redirect sea segura.
   *
   * Previene:
   * - Open redirects a dominios externos
   * - Loops de redirección (login → login)
   * - XSS via javascript: o data: URLs
   *
   * @param url URL a validar
   * @returns true si es segura, false si no
   */
  private isValidRedirectUrl(url: string): boolean {
    try {
      // Prevenir loops de redirección
      if (url.includes('/onboarding/login') ||
          url.includes('/onboarding/register') ||
          url.includes('/onboarding/forgot')) {
        return false;
      }

      // Si es URL absoluta, verificar que sea del mismo dominio
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return urlObj.origin === window.location.origin;
      }

      // Si es URL relativa, debe empezar con /
      if (!url.startsWith('/')) {
        return false;
      }

      // Prevenir XSS
      if (url.toLowerCase().includes('javascript:') ||
          url.toLowerCase().includes('data:')) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }
}
